
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, MoreHorizontal, Plus, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Plan } from '@/api/plans';
import { AddPlanDialog } from './add-plan-dialog';
import { EditPlanDialog } from './edit-plan-dialog';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { useGym } from '@/contexts/gym-context';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useConfirmationStore } from '@/lib/stores/confirmation-store';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { TableEmptyRow, TableLoadingRows } from '@/components/shared/table-states';

export function PlanTable() {
  const { t } = useTranslation();
  const { selectedGym } = useGym();
  const { toast } = useToast();
  const confirmationStore = useConfirmationStore();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  // Fetch plans manually
  const fetchPlans = useCallback(
    async (gymId: string) => {
      if (!gymId) return;

      setIsLoading(true);
      setError(null);

      try {
        const url = `/plans`;
        console.log(`Manually fetching plans from: ${url}`);

        const response = await apiClient.get(url);
        setPlans(response.data || []);
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load plans. Please try again.');
        setPlans([]);

        toast({
          title: 'Error',
          description: 'Failed to load plans. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [toast]
  );

  // Fetch plans when the component mounts or when the gym changes
  useEffect(() => {
    if (selectedGym) {
      console.log(`Selected gym changed to ${selectedGym}, fetching plans`);
      fetchPlans(selectedGym);
    }
  }, [selectedGym, fetchPlans]);

  // Handle plan deletion
  const handleDeletePlan = useCallback(
    (plan: Plan) => {
      confirmationStore.open({
        title: t('plans.deletePlan'),
        message: t('plans.deleteConfirmNamed', { name: plan.name }),
        variant: 'destructive',
        confirmLabel: t('common.delete'),
        onConfirm: async () => {
          try {
            console.log(`Deleting plan ${plan._id}...`);
            await apiClient.delete(`/plans/${plan._id}`);

            toast({
              title: t('common.success'),
              description: t('plans.planDeleted'),
            });

            if (selectedGym) {
              fetchPlans(selectedGym);
            }
          } catch (error) {
            console.error(`Error deleting plan ${plan._id}:`, error);

            toast({
              title: t('common.error'),
              description: t('plans.deleteError'),
              variant: 'destructive',
            });
          }
        },
      });
    },
    [fetchPlans, selectedGym, t, toast, confirmationStore]
  );

  const columns: ColumnDef<Plan>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.name')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    {
      accessorKey: 'price',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.price')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div>{formatCurrency(row.original.price, row.original.currency)}</div>
      ),
    },
    {
      accessorKey: 'duration',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.duration')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const plan = row.original;
        if(plan.durationType === 'fixed'){
          return <div>{t('plans.fixed')}</div>;
        }
        const durationText =
          plan.duration === 1
            ? t(`plans.${plan.durationType.slice(0, -1)}`) // Remove 's' for singular
            : t(`plans.${plan.durationType}`);
        return <div>{`${plan.duration} ${durationText}`}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.status')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <div
            className={`font-medium ${
              status === 'active' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {status === 'active' ? t('plans.active') : t('plans.inactive')}
          </div>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const plan = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">{t('common.openMenu')}</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedPlan(plan);
                  setEditDialogOpen(true);
                }}
              >
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeletePlan(plan)}
                className="text-red-600"
              >
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: plans,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  if (!selectedGym) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-10">
            <p className="text-muted-foreground">{t('plans.selectGymFirst')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Input
            placeholder={t('plans.filterByName')}
            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('name')?.setFilterValue(event.target.value)
            }
            className="w-full sm:max-w-sm"
          />
          <Button onClick={() => setAddDialogOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" /> {t('plans.addPlan')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingRows colSpan={columns.length} />
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-destructive"
                >
                  {error}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableEmptyRow colSpan={columns.length} message={t('plans.noPlans')} />
            )}
          </TableBody>
        </Table>
        <DataTablePagination
          page={table.getState().pagination.pageIndex + 1}
          pageCount={table.getPageCount()}
          total={table.getFilteredRowModel().rows.length}
          onPageChange={(p) => table.setPageIndex(p - 1)}
        />
      </CardContent>

      {/* Dialogs */}
      <AddPlanDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={() => {
          if (selectedGym) fetchPlans(selectedGym);
        }}
      />

      {editDialogOpen && selectedPlan && (
        <EditPlanDialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) {
              setSelectedPlan(null);
              if (selectedGym) fetchPlans(selectedGym);
            }
          }}
          plan={selectedPlan}
        />
      )}
    </Card>
  );
}
